import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatAmount } from '../../../lib/money';
import { Badge, Card, DataTable, EmptyState, KpiTile, Spinner, useToast } from '../../../ui/components';
import { Icon } from '../../../ui/icons';
import { useT } from '../../../i18n';
import { suppliersApi } from '../suppliersApi';
import { poStatusMeta } from '../../purchase-orders/purchaseOrdersApi';

const fmtMoney = (n, currency) => `${currency} ${formatAmount(n, 0)}`;
const fmtMoney2 = (n, currency) => `${currency} ${formatAmount(n)}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short' });
};

export default function SupplierSpendTab({ supplierId }) {
  const toast = useToast();
  const t = useT();
  const [spend, setSpend] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSpend(await suppliersApi.spend(supplierId));
    } catch (err) {
      toast.error(t('Could not load spend'), { description: err?.message });
    } finally {
      setLoading(false);
    }
  }, [supplierId, toast, t]);

  useEffect(() => { load(); }, [load]);

  const ccy = spend?.currency || 'USD';
  const trend = useMemo(() => spend?.monthlyTrend || [], [spend]);
  const maxTrend = useMemo(() => Math.max(1, ...trend.map((m) => Number(m.total) || 0)), [trend]);
  const maxCat = useMemo(() => Math.max(1, ...(spend?.topCategories || []).map((c) => Number(c.total) || 0)), [spend]);

  if (loading) {
    return <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-12)' }}><Spinner size={24} /></div>;
  }

  const hasAnything = spend && (spend.orderCount > 0 || spend.openOrdersCount > 0);
  if (!hasAnything) {
    return (
      <EmptyState
        icon={<Icon.BarChart size={28} />}
        title={t('No spend to report yet')}
        description={t('Once purchase orders are approved or sent to this supplier, spend analytics will appear here.')}
      />
    );
  }

  const itemColumns = [
    { key: 'desc', header: t('Item'), width: 280, render: (r) => <span style={{ fontWeight: 600 }}>{r.description}</span> },
    { key: 'qty', header: t('Qty ordered'), width: 130, align: 'right', render: (r) => Number(r.quantity).toLocaleString() },
    { key: 'total', header: t('Spend'), width: 150, align: 'right', render: (r) => <span style={{ fontWeight: 600 }}>{fmtMoney2(r.total, ccy)}</span> },
  ];

  return (
    <div className="sp-spend">
      {spend.hasMixedCurrencies && (
        <div className="sp-spend__notice">
          <Icon.Info size={15} />
          <span>{t('Figures shown in')} <strong>{ccy}</strong>. {t('{count} orders in other currencies are excluded.', { count: spend.otherCurrencyOrderCount })}</span>
        </div>
      )}

      <div className="sp-kpis">
        <KpiTile label={t('Total spend')} value={fmtMoney(spend.totalSpend, ccy)} meta={t('{count} committed orders', { count: spend.orderCount })} />
        <KpiTile label={t('Average order')} value={fmtMoney(spend.averageOrderValue, ccy)} />
        <KpiTile label={t('Largest order')} value={fmtMoney(spend.largestOrderValue, ccy)} />
        <KpiTile label={t('Open pipeline')} value={fmtMoney(spend.openOrdersValue, ccy)} meta={t('{count} draft/pending', { count: spend.openOrdersCount })} />
        <KpiTile label={t('First order')} value={fmtDate(spend.firstOrderDate)} />
        <KpiTile label={t('Last order')} value={fmtDate(spend.lastOrderDate)} />
      </div>

      <Card>
        <Card.Header><Card.Title>{t('Spend over the last 12 months')}</Card.Title></Card.Header>
        <Card.Body>
          <div className="sp-spend__chart">
            {trend.map((m) => {
              const h = Math.round(((Number(m.total) || 0) / maxTrend) * 100);
              return (
                <div className="sp-spend__bar-col" key={m.month} title={t('{month}: {amount} ({count} orders)', { month: m.month, amount: fmtMoney2(m.total, ccy), count: m.orderCount })}>
                  <div className="sp-spend__bar-track">
                    <div className="sp-spend__bar" style={{ height: `${h}%` }} />
                  </div>
                  <span className="sp-spend__bar-label">{monthLabel(m.month)}</span>
                </div>
              );
            })}
          </div>
        </Card.Body>
      </Card>

      <div className="sp-spend__grid">
        <Card>
          <Card.Header><Card.Title>{t('Spend by category')}</Card.Title></Card.Header>
          <Card.Body>
            {(spend.topCategories || []).length === 0 ? (
              <span className="pp-cell-muted">{t('No categorized spend.')}</span>
            ) : (
              <div className="sp-spend__cats">
                {spend.topCategories.map((c) => (
                  <div className="sp-spend__cat" key={c.categoryId || c.categoryName}>
                    <div className="sp-spend__cat-head">
                      <span>{c.categoryName}</span>
                      <span className="sp-spend__cat-val">{fmtMoney2(c.total, ccy)}</span>
                    </div>
                    <div className="sp-spend__cat-track">
                      <div className="sp-spend__cat-fill" style={{ width: `${Math.round(((Number(c.total) || 0) / maxCat) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header><Card.Title>{t('Orders by status')}</Card.Title></Card.Header>
          <Card.Body>
            <div className="sp-spend__statuses">
              {(spend.statusBreakdown || []).map((s) => {
                const m = poStatusMeta(s.status);
                return (
                  <div className="sp-spend__status" key={s.status}>
                    <Badge tone={m.tone} dot>{t(m.label)}</Badge>
                    <span className="sp-spend__status-count">{t('{count} orders', { count: s.count })}</span>
                    <span className="sp-spend__status-total">{fmtMoney2(s.total, ccy)}</span>
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      </div>

      <Card>
        <Card.Header><Card.Title>{t('Top items by spend')}</Card.Title></Card.Header>
        <Card.Body>
          {(spend.topItems || []).length === 0 ? (
            <span className="pp-cell-muted">{t('No committed line items yet.')}</span>
          ) : (
            <DataTable columns={itemColumns} data={spend.topItems} rowKey={(r) => r.itemId || r.description} density="comfortable" />
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
